import { showToast } from '@slices/apiSlice';
import { baseApi } from '@store/baseQuery';

import {
    AvailabilityForDateResponse,
    AvailabilityListResponse,
    AvailabilityRequestPayload,
    AvailabilityResponse,
    BlackoutDateRequestPayload,
    BlackoutDateResponse,
    BlackoutDateUpdateRequestPayload,
    BlackoutDatesListRequest,
    BlackoutDatesListResponse,
    DeleteAvailabiltySlotRequestPayload,
    GenericMessageResponse,
} from '../types';

export const availabilityApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        mentorAvailability: builder.mutation<AvailabilityResponse, AvailabilityRequestPayload>({
            query: body => ({
                url: '/mentor/availability',
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Availability', 'Personal'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    if (data) {
                        dispatch(
                            showToast({
                                variant: 'success',
                                description: 'Availability updated successfully',
                            })
                        );
                    }
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        getMentorAvailability: builder.query<AvailabilityListResponse, { username: string }>({
            query: ({ username }) => ({
                url: `mentor/${username}/availability`,
                method: 'GET',
            }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
            providesTags: ['Availability'],
            keepUnusedDataFor: 0,
        }),
        getMentorAvailabilityIntervals: builder.query<
            AvailabilityForDateResponse,
            { username: string; date: string }
        >({
            query: ({ username, date }) => ({
                url: `mentor/${username}/availability-intervals?date=${date}`,
                method: 'GET',
            }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
            keepUnusedDataFor: 0,
        }),
        deleteMentorAvailabilitySlot: builder.mutation<
            GenericMessageResponse,
            DeleteAvailabiltySlotRequestPayload
        >({
            query: body => ({
                url: `/mentor/availability/${body.availabilityId}/availability-slots/${body.availabilitySlotId}`,
                method: 'DELETE',
                body,
            }),
            invalidatesTags: ['Availability'],
        }),

        addBlackoutDates: builder.mutation<BlackoutDateResponse, BlackoutDateRequestPayload>({
            query: body => ({
                url: '/mentor/blackout',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Blackout'],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    if (data) {
                        dispatch(
                            showToast({
                                variant: 'success',
                                description: 'Blackout date added successfully',
                            })
                        );
                    }
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        updateBlackoutDate: builder.mutation<
            BlackoutDateResponse,
            BlackoutDateUpdateRequestPayload
        >({
            query: ({ body, id }) => ({
                url: `/mentor/blackout/${id}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Blackout'],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    if (data) {
                        dispatch(
                            showToast({
                                variant: 'success',
                                description: 'Blackout date update successfully',
                            })
                        );
                    }
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        getBlackoutDates: builder.query<BlackoutDatesListResponse, BlackoutDatesListRequest>({
            query: ({ offset, limit, orderBy, orderDirection, username }) => ({
                url: `/mentor/${username}/blackout`,
                method: 'GET',
                params: {
                    offset: offset ?? 0,
                    limit: limit ?? 5,
                    orderBy: orderBy ?? 'createdAt',
                    orderDirection: orderDirection ?? '--',
                },
            }),
            keepUnusedDataFor: 0,
            providesTags: ['Blackout'],
            async onQueryStarted(arg: any, { dispatch, queryFulfilled }: any) {
                try {
                    await queryFulfilled;
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
        deleteBlackoutDate: builder.mutation<GenericMessageResponse, { id: string }>({
            query: args => ({
                url: `/mentor/blackout/${args.id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Blackout'],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        showToast({
                            variant: 'success',
                            description: 'Blackout date deleted successfully',
                        })
                    );
                } catch (error) {
                    dispatch(showToast({ variant: 'error', description: error?.error }));
                }
            },
        }),
    }),
    overrideExisting: true,
});

export const {
    useMentorAvailabilityMutation,
    useGetMentorAvailabilityQuery,
    useLazyGetMentorAvailabilityIntervalsQuery,
    useDeleteMentorAvailabilitySlotMutation,
    useAddBlackoutDatesMutation,
    useUpdateBlackoutDateMutation,
    useGetBlackoutDatesQuery,
    useDeleteBlackoutDateMutation,
} = availabilityApi;
